/**
 * Admin: Reply Templates
 * ---------------------------------------------------------------------------
 * CRUD for reusable reply drafts. Templates support {{company}} and
 * {{trucks}} tokens which auto-substitute when applied to a hot reply.
 *
 * Backend endpoints:
 *   - GET    /api/admin/leads/replies/templates
 *   - POST   /api/admin/leads/replies/templates
 *   - PUT    /api/admin/leads/replies/templates/{id}
 *   - DELETE /api/admin/leads/replies/templates/{id}
 *   - POST   /api/admin/leads/replies/templates/{id}/apply/{reply_id}
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Files, Plus, FloppyDisk, Trash, ArrowClockwise, Copy, PencilSimple, X as XIcon,
  Lightning, DownloadSimple,
} from "@phosphor-icons/react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";

const _STARTER_TEMPLATES = [
  {
    name: "15-min demo",
    subject: "Re: quick look at FreightPilot?",
    body: "Thanks for hitting reply, {{company}}. Want a 15-min demo this week so I can show you the AI dispatch + rate negotiator on a real load? Pick a slot: https://cal.com/mikele/15min\n\nMIKELE",
  },
  {
    name: "Not interested — good luck",
    subject: "Re: quick look at FreightPilot?",
    body: "Appreciate the honest reply. If you change your mind, I'll be here. Godspeed on the freight.\n\nMIKELE",
  },
  {
    name: "Send Loom",
    subject: "Re: quick look at FreightPilot?",
    body: "Here's a 90-second walkthrough for {{company}}: https://loom.com/... — takes literally 90 seconds. If it makes sense, hit reply and I'll spin up a trial account.\n\nMIKELE",
  },
  {
    name: "Follow up in 30 days",
    subject: "Re: quick look at FreightPilot?",
    body: "Got it — I'll circle back in 30 days when things settle. If anything changes before that, my inbox is always open.\n\nMIKELE",
  },
];

export default function AdminReplyTemplatesPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("templates"); // "templates" | "rules"
  const [templates, setTemplates] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template
  const [creating, setCreating] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [creatingRule, setCreatingRule] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([
        client.get("/admin/leads/replies/templates"),
        client.get("/admin/leads/replies/auto-rules"),
      ]);
      setTemplates(t.data.templates || []);
      setRules(r.data.rules || []);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    try {
      if (payload.id) {
        await client.put(`/admin/leads/replies/templates/${payload.id}`, {
          name: payload.name, subject: payload.subject, body: payload.body,
        });
        toast.success("Template updated");
      } else {
        await client.post("/admin/leads/replies/templates", payload);
        toast.success("Template created");
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const saveRule = async (payload) => {
    try {
      if (payload.id) {
        await client.put(`/admin/leads/replies/auto-rules/${payload.id}`, payload);
        toast.success("Rule updated");
      } else {
        await client.post("/admin/leads/replies/auto-rules", payload);
        toast.success("Rule created");
      }
      setEditingRule(null);
      setCreatingRule(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const removeTemplate = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await client.delete(`/admin/leads/replies/templates/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const removeRule = async (id, name) => {
    if (!window.confirm(`Delete rule "${name}"?`)) return;
    try {
      await client.delete(`/admin/leads/replies/auto-rules/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const seedStarters = async () => {
    if (!window.confirm("Load the 10 pre-approved reply templates? Existing templates with the same name will be skipped.")) return;
    setSeeding(true);
    try {
      const { data } = await client.post("/admin/leads/replies/templates/seed-defaults");
      if (data.seeded === 0) {
        toast.info(`All ${data.total_defaults} defaults are already loaded — nothing to add.`);
      } else {
        toast.success(`Loaded ${data.seeded} new templates${data.skipped_existing ? ` (${data.skipped_existing} already existed)` : ""}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  if (!user?.is_admin) {
    return <div className="p-8 text-red-400" data-testid="admin-reply-templates-forbidden">Admin only.</div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-reply-templates-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/30 bg-[#FF5A1F]/10 text-[#FF5A1F] px-3 py-1 text-xs font-semibold mb-3">
            <Files size={12} weight="fill" /> REPLY TEMPLATES
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">
            Approve replies in one click
          </h1>
          <p className="text-neutral-400 mt-2 text-sm sm:text-base max-w-2xl">
            Save your best canned responses ("15-min demo", "send Loom", "not interested — good luck") and apply them to any hot reply from the picker on Admin → Cold Leads.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {templates.length < 10 && (
            <button
              data-testid="seed-starters-btn"
              onClick={seedStarters}
              disabled={seeding}
              className="rounded-md px-3 py-2 text-xs font-semibold bg-white/[0.05] border border-white/10 hover:border-white/25 text-neutral-300 disabled:opacity-50 flex items-center gap-1.5"
            >
              <Copy size={12} weight="fill" /> {templates.length === 0 ? "Load 10 approved defaults" : "Top up defaults"}
            </button>
          )}
          <button
            data-testid="new-template-btn"
            onClick={() => setCreating(true)}
            className="rounded-md px-4 py-2 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] flex items-center gap-1.5"
          >
            <Plus size={14} weight="bold" /> New template
          </button>
        </div>
      </div>

      {/* Token help */}
      <div className="text-[11px] text-neutral-500 leading-relaxed p-3 rounded-md bg-white/[0.02] border border-white/5">
        <b className="text-neutral-400">Personalization tokens:</b> use{" "}
        <code className="text-[#FF9764] bg-black/40 px-1 rounded">{"{{company}}"}</code> or{" "}
        <code className="text-[#FF9764] bg-black/40 px-1 rounded">{"{{trucks}}"}</code> in the subject or body — they'll auto-substitute with the lead's real values when the template is applied.
      </div>

      {/* Tab strip */}
      <div className="flex items-center gap-2" data-testid="reply-templates-tabs">
        <button
          data-testid="tab-templates"
          onClick={() => setTab("templates")}
          className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
            tab === "templates"
              ? "bg-[#FF5A1F]/15 border border-[#FF5A1F]/40 text-white"
              : "bg-white/[0.03] border border-white/10 text-neutral-400 hover:text-white"
          }`}
        >
          <Files size={12} weight="fill" className="inline mr-1.5" />
          Templates <span className="text-[10px] text-neutral-500">({templates.length})</span>
        </button>
        <button
          data-testid="tab-rules"
          onClick={() => setTab("rules")}
          className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
            tab === "rules"
              ? "bg-[#FF5A1F]/15 border border-[#FF5A1F]/40 text-white"
              : "bg-white/[0.03] border border-white/10 text-neutral-400 hover:text-white"
          }`}
        >
          <Lightning size={12} weight="fill" className="inline mr-1.5" />
          Auto-rules <span className="text-[10px] text-neutral-500">({rules.length})</span>
        </button>
      </div>

      {tab === "templates" ? (
        <TemplatesView
          loading={loading}
          templates={templates}
          onEdit={setEditing}
          onDelete={removeTemplate}
        />
      ) : (
        <RulesView
          loading={loading}
          rules={rules}
          templates={templates}
          onEdit={setEditingRule}
          onCreate={() => setCreatingRule(true)}
          onDelete={removeRule}
          onRefresh={load}
        />
      )}

      {/* Modals */}
      {(creating || editing) && (
        <TemplateEditor
          template={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSave={save}
        />
      )}
      {(creatingRule || editingRule) && (
        <RuleEditor
          rule={editingRule}
          templates={templates}
          onClose={() => { setCreatingRule(false); setEditingRule(null); }}
          onSave={saveRule}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TemplatesView — the existing template card grid, refactored into its own
// component so we can tab between it and the auto-rules view.
// ---------------------------------------------------------------------------
function TemplatesView({ loading, templates, onEdit, onDelete }) {
  if (loading) return <div className="text-neutral-500 text-sm">Loading…</div>;
  if (templates.length === 0) {
    return (
      <div className="card-surface p-8 text-center" data-testid="empty-templates">
        <Files size={40} weight="thin" className="mx-auto text-neutral-600 mb-3" />
        <div className="text-white font-semibold">No templates yet</div>
        <div className="text-xs text-neutral-500 mt-1 max-w-md mx-auto">
          Save your best responses so future hot replies get answered in one click. Click "Add starter set" above for 4 pre-written examples.
        </div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {templates.map((t) => (
        <TemplateCard
          key={t.id}
          template={t}
          onEdit={() => onEdit(t)}
          onDelete={() => onDelete(t.id, t.name)}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RulesView — auto-send template rules. Each rule matches on sentiment
// and/or keyword regex; action determines whether the template gets
// applied (drafted) or fully sent without human review.
// ---------------------------------------------------------------------------
function RulesView({ loading, rules, templates, onEdit, onCreate, onDelete, onRefresh }) {
  if (loading) return <div className="text-neutral-500 text-sm">Loading…</div>;
  const templateById = Object.fromEntries(templates.map((t) => [t.id, t]));

  return (
    <>
      <div className="rounded-md p-3 bg-yellow-500/[0.05] border border-yellow-500/30 text-yellow-200 text-[12px] leading-relaxed" data-testid="rules-warning">
        <b className="font-semibold">⚠️ Auto-rules fire without human review.</b> Test with the "apply (draft only)" action first — it drops the reply into the pending draft box for you to approve. Only switch to "send" once you trust the rule.
      </div>

      <div className="flex justify-end">
        <button
          data-testid="new-rule-btn"
          onClick={onCreate}
          disabled={templates.length === 0}
          className="rounded-md px-4 py-2 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 flex items-center gap-1.5"
        >
          <Plus size={14} weight="bold" /> New rule
        </button>
      </div>

      {templates.length === 0 && (
        <div className="text-xs text-neutral-500 text-center py-4">
          Create at least one template first — rules point at templates.
        </div>
      )}

      {rules.length === 0 && templates.length > 0 ? (
        <div className="card-surface p-8 text-center" data-testid="empty-rules">
          <Lightning size={40} weight="thin" className="mx-auto text-neutral-600 mb-3" />
          <div className="text-white font-semibold">No auto-rules yet</div>
          <div className="text-xs text-neutral-500 mt-1 max-w-md mx-auto">
            Automate the boring replies. E.g., <b>if sentiment = not_interested → apply "Not interested — good luck" template</b>.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map((r) => (
            <RuleCard
              key={r.id}
              rule={r}
              template={templateById[r.template_id]}
              onEdit={() => onEdit(r)}
              onDelete={() => onDelete(r.id, r.name)}
              onRefresh={onRefresh}
            />
          ))}
        </div>
      )}
    </>
  );
}

function RuleCard({ rule, template, onEdit, onDelete, onRefresh }) {
  const [simulating, setSimulating] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [showSim, setShowSim] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [showFireLog, setShowFireLog] = useState(false);
  const [fireLog, setFireLog] = useState(null);
  const [loadingFireLog, setLoadingFireLog] = useState(false);

  const toggleFireLog = async () => {
    const nextOpen = !showFireLog;
    setShowFireLog(nextOpen);
    if (nextOpen && !fireLog) {
      setLoadingFireLog(true);
      try {
        const { data } = await client.get(`/admin/leads/replies/auto-rules/${rule.id}/fire-log?limit=5`);
        setFireLog(data);
      } catch (e) {
        toast.error("Failed to load fire log");
        setShowFireLog(false);
      } finally {
        setLoadingFireLog(false);
      }
    }
  };

  const downloadCsv = async () => {
    try {
      const resp = await client.get(
        `/admin/leads/replies/auto-rules/${rule.id}/fire-log/export.csv?limit=200`,
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeName = (rule.name || "rule").replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 40);
      link.download = `fire-log-${safeName}-${rule.id.slice(0, 8)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Fire log CSV downloaded");
    } catch (e) {
      toast.error("CSV export failed");
    }
  };

  const toggleEnabled = async () => {
    setToggling(true);
    try {
      const { data } = await client.patch(`/admin/leads/replies/auto-rules/${rule.id}/toggle`);
      toast.success(data.enabled ? "Rule enabled" : "Rule paused");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setToggling(false);
    }
  };

  const runSimulate = async () => {
    setSimulating(true);
    setShowSim(true);
    try {
      const { data } = await client.post("/admin/leads/replies/auto-rules/simulate", {
        template_id: rule.template_id,
        action: rule.action,
        sentiment_match: rule.sentiment_match,
        keyword_regex: rule.keyword_regex,
        sample_size: 30,
      });
      setSimResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulator failed");
      setShowSim(false);
    } finally {
      setSimulating(false);
    }
  };

  const sentimentLabels = (rule.sentiment_match || []).map((s) => ({
    interested: "🟢 Interested",
    maybe: "🟡 Maybe",
    not_interested: "🔴 Not interested",
    auto_reply: "⚪ Auto-reply",
  })[s] || s);
  return (
    <>
    <div
      className="card-surface p-4 flex items-start gap-3 flex-wrap"
      data-testid={`rule-card-${rule.id}`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
        rule.action === "send"
          ? "bg-red-500/15 border border-red-500/40"
          : "bg-blue-500/15 border border-blue-500/40"
      }`}>
        <Lightning size={16} weight="fill" className={rule.action === "send" ? "text-red-400" : "text-blue-400"} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-white font-semibold text-sm">{rule.name}</h3>
          <button
            type="button"
            onClick={toggleEnabled}
            disabled={toggling}
            data-testid={`rule-toggle-${rule.id}`}
            title={rule.enabled ? "Click to pause this rule" : "Click to enable this rule"}
            className={`text-[9px] uppercase tracking-widest border rounded-full px-1.5 py-0.5 font-semibold transition-colors disabled:opacity-60 cursor-pointer hover:opacity-90 ${
              rule.enabled
                ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
                : "bg-neutral-700/40 text-neutral-500 border-white/10"
            }`}
          >
            {toggling ? "…" : (rule.enabled ? "● LIVE" : "○ OFF")}
          </button>
          <span className={`text-[9px] uppercase tracking-widest border rounded-full px-1.5 py-0.5 font-semibold ${
            rule.action === "send"
              ? "bg-red-500/15 text-red-300 border-red-500/40"
              : "bg-blue-500/15 text-blue-300 border-blue-500/40"
          }`}>
            {rule.action === "send" ? "AUTO-SEND" : "AUTO-DRAFT"}
          </span>
          {rule.sms_on_fire && (
            <span
              className="text-[9px] uppercase tracking-widest bg-orange-500/15 text-orange-300 border border-orange-500/40 rounded-full px-1.5 py-0.5 font-semibold"
              title="Fires an instant SMS to admins on every match"
              data-testid={`rule-sms-badge-${rule.id}`}
            >
              📱 SMS
            </span>
          )}
        </div>
        <div className="text-[11px] text-neutral-400 mt-1 flex flex-wrap gap-x-3 gap-y-1">
          <span>
            <b className="text-neutral-500">If</b>{" "}
            {sentimentLabels.length > 0 ? sentimentLabels.join(" / ") : "any sentiment"}
            {rule.keyword_regex && (
              <> · matches <code className="bg-black/30 rounded px-1 text-[#FF9764]">{rule.keyword_regex}</code></>
            )}
          </span>
          <span>
            <b className="text-neutral-500">Then</b>{" "}
            {rule.action === "send" ? "send" : "draft"}{" "}
            <b className="text-white">"{template?.name || "?"}"</b>
          </span>
        </div>
        {rule.hit_count > 0 && (
          <div className="text-[10px] text-neutral-600 mt-1">
            Fired {rule.hit_count}× · priority {rule.priority}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={toggleFireLog}
          data-testid={`rule-firelog-${rule.id}`}
          className={`p-1.5 rounded flex items-center gap-1 text-[11px] font-semibold px-2 transition-colors ${
            showFireLog
              ? "bg-orange-500/15 text-orange-300 border border-orange-500/40"
              : "hover:bg-white/[0.05] text-neutral-400 hover:text-white"
          }`}
          title="View the last 5 replies this rule fired on"
        >
          {loadingFireLog ? (
            <ArrowClockwise size={12} className="animate-spin" />
          ) : (
            <Files size={12} />
          )}
          Log
        </button>
        <button
          onClick={runSimulate}
          disabled={simulating}
          data-testid={`rule-simulate-${rule.id}`}
          className="p-1.5 rounded hover:bg-blue-500/10 text-neutral-400 hover:text-blue-300 disabled:opacity-50 flex items-center gap-1 text-[11px] font-semibold px-2"
          title="Dry-run this rule against the last 30 replies"
        >
          {simulating ? (
            <ArrowClockwise size={12} className="animate-spin" />
          ) : (
            <Lightning size={12} weight="fill" />
          )}
          Test
        </button>
        <button
          onClick={onEdit}
          data-testid={`rule-edit-${rule.id}`}
          className="p-1.5 rounded hover:bg-white/[0.05] text-neutral-400 hover:text-white"
          title="Edit"
        >
          <PencilSimple size={13} />
        </button>
        <button
          onClick={onDelete}
          data-testid={`rule-delete-${rule.id}`}
          className="p-1.5 rounded hover:bg-red-500/10 text-neutral-400 hover:text-red-400"
          title="Delete"
        >
          <Trash size={13} />
        </button>
      </div>
    </div>
    {showSim && (
      <RuleSimulatorResult
        result={simResult}
        loading={simulating}
        onClose={() => { setShowSim(false); setSimResult(null); }}
      />
    )}
    {showFireLog && (
      <RuleFireLogPanel
        log={fireLog}
        loading={loadingFireLog}
        onClose={() => setShowFireLog(false)}
        onDownloadCsv={downloadCsv}
      />
    )}
    </>
  );
}

// ---------------------------------------------------------------------------
// RuleFireLogPanel — mini timeline of the last N replies this rule fired on.
// Sits below the rule card, styled like a compact activity feed.
// ---------------------------------------------------------------------------
function RuleFireLogPanel({ log, loading, onClose, onDownloadCsv }) {
  if (loading && !log) {
    return (
      <div className="mt-2 p-4 rounded-lg border border-orange-500/30 bg-orange-500/[0.05] text-xs text-neutral-400 flex items-center gap-2" data-testid="rule-firelog-loading">
        <ArrowClockwise size={12} className="animate-spin" /> Loading fire history…
      </div>
    );
  }
  if (!log) return null;

  return (
    <div className="mt-2 p-4 rounded-lg border border-orange-500/30 bg-orange-500/[0.04]" data-testid="rule-firelog-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Files size={14} className="text-orange-400" />
          <span className="text-xs font-semibold text-white">Fire history</span>
          <span className="text-[11px] text-neutral-400" data-testid="rule-firelog-total">
            {log.total > 0
              ? `Last ${log.events.length} of ${log.total} total fire${log.total === 1 ? "" : "s"}`
              : "Never fired yet"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {log.total > 0 && onDownloadCsv && (
            <button
              onClick={onDownloadCsv}
              data-testid="rule-firelog-csv-btn"
              className="text-[10px] font-semibold px-2.5 py-1 rounded-md border border-white/10 hover:border-orange-500/40 hover:text-orange-300 text-neutral-300 flex items-center gap-1"
              title="Download last 200 fires as CSV"
            >
              <DownloadSimple size={11} weight="bold" /> CSV
            </button>
          )}
          <button
            onClick={onClose}
            data-testid="rule-firelog-close"
            className="text-neutral-500 hover:text-white text-xs"
          >
            <XIcon size={14} />
          </button>
        </div>
      </div>

      {log.events.length === 0 ? (
        <div className="text-[11px] text-neutral-500 py-2" data-testid="rule-firelog-empty">
          This rule has never fired. Once a matching reply comes in, it'll appear here.
        </div>
      ) : (
        <div className="space-y-2">
          {log.events.map((ev, idx) => (
            <div
              key={ev.id || idx}
              data-testid={`firelog-event-${idx}`}
              className="rounded-md bg-white/[0.03] border border-white/10 px-3 py-2 text-[11px] flex items-start gap-3"
            >
              <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                ev.sent
                  ? "bg-emerald-400"
                  : ev.error
                    ? "bg-red-400"
                    : "bg-blue-400"
              }`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <b className="text-white">{ev.company}</b>
                  {ev.power_units && <span className="text-neutral-500">{ev.power_units} trucks</span>}
                  {ev.state && <span className="text-neutral-500">· {ev.state}</span>}
                  <span className="ml-auto text-neutral-500 text-[10px]">
                    {ev.fired_at && new Date(ev.fired_at).toLocaleString()}
                  </span>
                </div>
                <div className="text-neutral-400 truncate mt-0.5" title={ev.reply_subject}>
                  <span className="text-neutral-500">Re:</span> {ev.reply_subject || "(no subject)"}
                </div>
                <div className="flex items-center gap-2 mt-1 text-[9px]">
                  <span className={`uppercase tracking-widest border rounded-full px-1.5 py-0.5 font-semibold ${
                    ev.action === "send"
                      ? "bg-red-500/15 text-red-300 border-red-500/40"
                      : "bg-blue-500/15 text-blue-300 border-blue-500/40"
                  }`}>
                    {ev.action === "send" ? "SENT" : "DRAFTED"}
                  </span>
                  {ev.sentiment && (
                    <span className="text-neutral-500">Sentiment: {ev.sentiment}</span>
                  )}
                  {ev.error && (
                    <span className="text-red-400" title={ev.error}>· failed</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RuleSimulatorResult — inline preview showing which recent replies WOULD
// have matched this rule (dry run). Sits right below the rule card.
// ---------------------------------------------------------------------------
function RuleSimulatorResult({ result, loading, onClose }) {
  if (loading && !result) {
    return (
      <div className="mt-2 p-4 rounded-lg border border-blue-500/30 bg-blue-500/[0.05] text-xs text-neutral-400 flex items-center gap-2" data-testid="rule-simulator-loading">
        <ArrowClockwise size={12} className="animate-spin" /> Simulating against last 30 replies…
      </div>
    );
  }
  if (!result) return null;

  const rate = result.match_rate_pct;
  const rateColor = rate >= 20 ? "text-emerald-300" : rate >= 5 ? "text-yellow-300" : "text-neutral-400";

  return (
    <div className="mt-2 p-4 rounded-lg border border-blue-500/30 bg-blue-500/[0.05]" data-testid="rule-simulator-result">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Lightning size={14} weight="fill" className="text-blue-400" />
          <span className="text-xs font-semibold text-white">Dry-run result</span>
          <span className={`text-[11px] font-semibold ${rateColor}`} data-testid="sim-match-summary">
            {result.match_count} of {result.sample_size} replies would match ({rate}%)
          </span>
        </div>
        <button
          onClick={onClose}
          data-testid="rule-simulator-close"
          className="text-neutral-500 hover:text-white text-xs"
        >
          <XIcon size={14} />
        </button>
      </div>

      {result.matches.length === 0 ? (
        <div className="text-[11px] text-neutral-500 py-2" data-testid="sim-no-matches">
          No matches in the last {result.sample_size} replies. If this rule fired live, it wouldn't do anything yet — either widen the sentiment filter or loosen the regex.
        </div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin">
          {result.matches.map((m) => (
            <div
              key={m.reply_id}
              data-testid={`sim-match-${m.reply_id}`}
              className="rounded-md bg-white/[0.03] border border-white/10 px-3 py-2 text-[11px]"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <b className="text-white">{m.company}</b>
                {m.power_units && <span className="text-neutral-500">{m.power_units} trucks</span>}
                {m.sentiment && (
                  <span className="text-[9px] uppercase tracking-widest border rounded-full px-1.5 py-0.5 border-white/10 text-neutral-400">
                    {m.sentiment}
                  </span>
                )}
                <span className="text-neutral-500 ml-auto">{m.received_at && new Date(m.received_at).toLocaleDateString()}</span>
              </div>
              <div className="text-neutral-400 mt-1 truncate" title={m.subject}>
                <span className="text-neutral-500">Subj:</span> {m.subject || "(no subject)"}
              </div>
              {m.snippet && (
                <div className="text-neutral-500 mt-1 line-clamp-2">
                  {m.snippet}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 text-[10px] text-neutral-500 italic">
        This is a preview. Nothing was sent or drafted. Enable the rule to have it fire on future replies.
      </div>
    </div>
  );
}

function RuleEditor({ rule, templates, onClose, onSave }) {
  const [name, setName] = useState(rule?.name || "");
  const [templateId, setTemplateId] = useState(rule?.template_id || templates[0]?.id || "");
  const [action, setAction] = useState(rule?.action || "apply");
  const [sentimentA, setSentimentA] = useState((rule?.sentiment_match || []).includes("interested"));
  const [sentimentM, setSentimentM] = useState((rule?.sentiment_match || []).includes("maybe"));
  const [sentimentN, setSentimentN] = useState((rule?.sentiment_match || []).includes("not_interested"));
  const [sentimentAR, setSentimentAR] = useState((rule?.sentiment_match || []).includes("auto_reply"));
  const [keywordRegex, setKeywordRegex] = useState(rule?.keyword_regex || "");
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [priority, setPriority] = useState(rule?.priority ?? 100);
  const [smsOnFire, setSmsOnFire] = useState(rule?.sms_on_fire ?? false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const sentiment_match = [
      sentimentA && "interested",
      sentimentM && "maybe",
      sentimentN && "not_interested",
      sentimentAR && "auto_reply",
    ].filter(Boolean);
    if (!name.trim()) return toast.error("Name required");
    if (!templateId) return toast.error("Pick a template");
    if (sentiment_match.length === 0 && !keywordRegex.trim()) {
      return toast.error("Pick at least one sentiment OR a keyword regex");
    }
    setSaving(true);
    try {
      await onSave({
        id: rule?.id,
        name: name.trim(),
        template_id: templateId,
        action,
        sentiment_match: sentiment_match.length ? sentiment_match : null,
        keyword_regex: keywordRegex.trim() || null,
        enabled,
        priority: parseInt(priority, 10) || 100,
        sms_on_fire: smsOnFire,
      });
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="rule-editor-modal"
      onClick={onClose}
    >
      <div className="card-surface w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold text-lg">{rule ? "Edit rule" : "New auto-rule"}</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><XIcon size={16} /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Rule name</label>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Auto-decline politely"
              data-testid="rule-name-input"
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Match sentiment</label>
            <div className="flex flex-wrap gap-2">
              {[
                { key: "interested", label: "🟢 Interested", state: sentimentA, setter: setSentimentA },
                { key: "maybe", label: "🟡 Maybe", state: sentimentM, setter: setSentimentM },
                { key: "not_interested", label: "🔴 Not interested", state: sentimentN, setter: setSentimentN },
                { key: "auto_reply", label: "⚪ Auto-reply", state: sentimentAR, setter: setSentimentAR },
              ].map((opt) => (
                <label key={opt.key} className="flex items-center gap-1.5 text-xs text-white cursor-pointer bg-white/[0.03] border border-white/10 rounded-md px-2 py-1.5 hover:border-white/25">
                  <input
                    type="checkbox"
                    checked={opt.state}
                    onChange={(e) => opt.setter(e.target.checked)}
                    data-testid={`rule-sentiment-${opt.key}`}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">
              Keyword regex <span className="text-neutral-600 normal-case">(optional, matches subject + body)</span>
            </label>
            <input
              type="text" value={keywordRegex} onChange={(e) => setKeywordRegex(e.target.value)}
              placeholder="unsubscribe|remove me|stop"
              data-testid="rule-keyword-input"
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60 font-mono"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Template</label>
              <select
                value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                data-testid="rule-template-select"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Action</label>
              <select
                value={action} onChange={(e) => setAction(e.target.value)}
                data-testid="rule-action-select"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
              >
                <option value="apply">Draft only (safe)</option>
                <option value="send">Auto-send (aggressive)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
              <input
                type="checkbox" checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                data-testid="rule-enabled"
              />
              Enabled
            </label>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Priority</label>
              <input
                type="number" value={priority}
                onChange={(e) => setPriority(e.target.value)}
                data-testid="rule-priority-input"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-1.5 px-2.5"
              />
            </div>
          </div>

          {/* SMS on fire toggle — Wave 10 */}
          <div className="mt-4 p-3 rounded-lg border border-white/10 bg-white/[0.02]">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={smsOnFire}
                onChange={(e) => setSmsOnFire(e.target.checked)}
                data-testid="rule-sms-on-fire"
                className="mt-0.5 accent-[#FF5A1F]"
              />
              <div className="flex-1">
                <div className="text-sm text-white font-semibold flex items-center gap-2">
                  📱 Instant SMS on fire
                </div>
                <div className="text-[11px] text-neutral-500 mt-0.5 leading-relaxed">
                  Text every admin the moment this rule fires — instead of waiting for the daily digest. Perfect for high-intent rules like <b className="text-emerald-300">🟢 Interested</b> replies. SMS uses your saved reply-alert phone in Settings.
                </div>
                {/* Wave 11: Send test SMS button */}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      try {
                        const { data } = await client.post("/admin/leads/replies/auto-rules/test-sms");
                        if (data.sent) {
                          toast.success(`Test SMS sent to ${data.phone}`);
                        } else {
                          toast.error(data.reason === "not_configured"
                            ? "Twilio not configured on the server"
                            : `Test failed: ${data.reason || "unknown"}`);
                        }
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Test SMS failed. Save a phone number in the Cold Leads → SMS panel first.");
                      }
                    }}
                    data-testid="rule-send-test-sms"
                    className="text-[11px] px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/10 text-neutral-300 hover:border-emerald-400/40 hover:text-emerald-300 transition-colors flex items-center gap-1.5"
                  >
                    📲 Send test SMS now
                  </button>
                  <span className="text-[10px] text-neutral-600">Verifies Twilio + phone BEFORE flipping ON</span>
                </div>
              </div>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-white/10">
          <button onClick={onClose} className="text-xs text-neutral-400 hover:text-white px-3 py-2">Cancel</button>
          <button
            onClick={handleSave} disabled={saving}
            data-testid="rule-save-btn"
            className="rounded-md px-4 py-2 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <ArrowClockwise size={13} className="animate-spin" /> : <FloppyDisk size={13} weight="fill" />}
            {saving ? "Saving…" : "Save rule"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TemplateCard({ template, onEdit, onDelete }) {
  return (
    <div className="card-surface p-4 flex flex-col" data-testid={`template-card-${template.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <h3 className="text-white font-semibold text-sm">{template.name}</h3>
          {template.use_count > 0 && (
            <div className="text-[10px] text-neutral-500 mt-0.5">
              Used {template.use_count}×
              {template.last_used_at && (
                <> · last {new Date(template.last_used_at).toLocaleDateString()}</>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onEdit}
            data-testid={`template-edit-${template.id}`}
            className="p-1.5 rounded hover:bg-white/[0.05] text-neutral-400 hover:text-white"
            title="Edit"
          >
            <PencilSimple size={13} />
          </button>
          <button
            onClick={onDelete}
            data-testid={`template-delete-${template.id}`}
            className="p-1.5 rounded hover:bg-red-500/10 text-neutral-400 hover:text-red-400"
            title="Delete"
          >
            <Trash size={13} />
          </button>
        </div>
      </div>
      <div className="text-[11px] text-neutral-400 mb-2 italic">
        "{template.subject}"
      </div>
      <div className="text-xs text-neutral-300 font-mono bg-black/30 rounded-md p-2.5 leading-relaxed whitespace-pre-wrap line-clamp-6">
        {template.body}
      </div>
    </div>
  );
}

function TemplateEditor({ template, onClose, onSave }) {
  const [name, setName] = useState(template?.name || "");
  const [subject, setSubject] = useState(template?.subject || "");
  const [body, setBody] = useState(template?.body || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim() || !subject.trim() || !body.trim()) {
      toast.error("Name, subject, and body are all required");
      return;
    }
    setSaving(true);
    try {
      await onSave({ id: template?.id, name: name.trim(), subject: subject.trim(), body: body.trim() });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="template-editor-modal"
      onClick={onClose}
    >
      <div
        className="card-surface w-full max-w-lg p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold text-lg">
            {template ? "Edit template" : "New template"}
          </h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-white">
            <XIcon size={16} />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="15-min demo"
              data-testid="template-name-input"
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Re: quick look at FreightPilot?"
              data-testid="template-subject-input"
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">
              Body <span className="text-neutral-600 normal-case">· use {"{{company}}"} / {"{{trucks}}"}</span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder="Thanks for hitting reply, {{company}}. Want a 15-min demo this week?"
              data-testid="template-body-input"
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60 font-mono leading-relaxed"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-white/10">
          <button onClick={onClose} className="text-xs text-neutral-400 hover:text-white px-3 py-2">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="template-save-btn"
            className="rounded-md px-4 py-2 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <ArrowClockwise size={13} className="animate-spin" /> : <FloppyDisk size={13} weight="fill" />}
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
